import {
  Helmet,
  HelmetProvider,
  HelmetServerState
} from "@dr.pogodin/react-helmet"
import React, { useEffect, useMemo, useState } from "react"

import merge from "lodash-es/merge"

import { connect, currentPage, urlFor } from "scrivito"
import { HomepageObjClass } from "scrivito/Objs/Homepage/HomepageObjClass"
import { DEFAULT_LOCALE } from "shared/constants/app-constants"
import { getCurrentSiteID } from "shared/lib/getCurrentSiteID"
import { useCurrentScrivitoPage } from "shared/lib/scrivito/scrivito-page-store"
import { getMetadata } from "shared/lib/seo/getMetadata"
import { useEvent } from "shared/lib/useEvent"
import { HelmetAttributes, HelmetContext } from "shared/types/HelmetContext"

export const helmetContext: { helmet?: HelmetServerState } = {}

const SCHAKO_TITLE_SUFFIX = "SCHAKOnext"

interface CustomHelmetProviderProps {
  children: React.ReactNode
  lang?: string
}

function CustomHelmetProviderComponent({
  children,
  lang
}: CustomHelmetProviderProps) {
  const page = currentPage()
  const homepage = HomepageObjClass.onSite(getCurrentSiteID()).all().first()

  const siteName = homepage?.get("title") || SCHAKO_TITLE_SUFFIX

  const { set } = useCurrentScrivitoPage()
  useEffect(() => {
    set(page?.id())
  }, [page?.id()])

  const [helmetAttributes, setHelmetAttributes] =
    useState<HelmetAttributes | null>({
      htmlAttributes: { lang: lang || DEFAULT_LOCALE }
    })

  const updateHelmetAttributes = useEvent((attributes: HelmetAttributes) => {
    setHelmetAttributes(prev => merge({}, prev || {}, attributes))
  })

  const resolvedHelmetAttributes = useMemo(() => {
    let title = helmetAttributes?.title
    let meta = helmetAttributes?.meta
    let link = helmetAttributes?.link
    if (!title) {
      title = siteName
    }
    if (!title.toLowerCase().includes(siteName.toLowerCase())) {
      title = `${title} | ${siteName}`
    }
    if (page && page.get("title")) {
      title = `${page.get("title")} - ${siteName}`
      meta = getMetadata(page, siteName)
      link = page
        ? [
            {
              rel: "canonical",
              href: urlFor(page),
              type: "text/html"
            }
          ]
        : []
    }

    return {
      ...helmetAttributes,
      title,
      meta,
      link
    }
  }, [helmetAttributes, page])

  return (
    <HelmetProvider
      onServerState={state => {
        helmetContext.helmet = state
      }}
    >
      <HelmetContext.Provider value={updateHelmetAttributes}>
        {children}
        <Helmet {...resolvedHelmetAttributes} />
      </HelmetContext.Provider>
    </HelmetProvider>
  )
}

export const CustomHelmetProvider = React.memo(
  connect(CustomHelmetProviderComponent)
) as typeof CustomHelmetProviderComponent
